import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Users, GraduationCap, UserCheck, Briefcase, Search, RefreshCw, 
  MessageCircle, Copy, Eye, EyeOff, Key, Mail, Phone, Download,
  Send, CheckCircle, XCircle, AlertTriangle, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserCredentialsPage = () => {
  const { getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('students');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Data
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [parents, setParents] = useState([]);
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  
  // Dialogs
  const [resetDialog, setResetDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [credentialResultDialog, setCredentialResultDialog] = useState(false);
  const [editEmailDialog, setEditEmailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [showPasswords, setShowPasswords] = useState({});
  
  // Credential result (shown after reset/create)
  const [credentialResult, setCredentialResult] = useState({
    name: '',
    login_id: '',
    email: '',
    password: '',
    phone: '',
    action: '' // 'reset' or 'create'
  });
  
  // Create credential form
  const [createForm, setCreateForm] = useState({
    person_id: '',
    person_type: 'student',
    email: '',
    phone: '',
    use_admission_number: true  // Default: use admission number for students
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, teachersRes, staffRes, usersRes, classesRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/staff`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/user-credentials`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() })
      ]);
      
      const studentsData = studentsRes.data?.items || studentsRes.data || [];
      setStudents(studentsData);
      setTeachers(teachersRes.data?.items || teachersRes.data || []);
      setStaff(staffRes.data?.items || staffRes.data || []);
      setUsers(usersRes.data || []);
      setClasses(classesRes.data || []);
      
      // Extract parents from students
      const parentsList = [];
      studentsData.forEach(s => {
        if (s.father_name) {
          parentsList.push({
            id: `parent-father-${s.id}`,
            name: s.father_name,
            phone: s.father_mobile,
            email: s.father_email,
            student_id: s.id,
            student_name: s.name,
            relation: 'Father'
          });
        }
        if (s.mother_name) {
          parentsList.push({
            id: `parent-mother-${s.id}`,
            name: s.mother_name,
            phone: s.mother_mobile,
            email: s.mother_email,
            student_id: s.id,
            student_name: s.name,
            relation: 'Mother'
          });
        }
      });
      setParents(parentsList);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getUserCredential = (personId, personType) => {
    // For parents, extract student_id from composite ID (parent-father-{student_id})
    if (personType === 'parent' && personId.startsWith('parent-')) {
      const studentId = personId.replace('parent-father-', '').replace('parent-mother-', '');
      // Find parent user that has this student in children array
      return users.find(u => 
        u.role === 'parent' && 
        u.children && 
        u.children.includes(studentId)
      );
    }
    
    return users.find(u => 
      (u.student_id === personId || u.teacher_id === personId || u.staff_id === personId || u.email === personId) &&
      (u.role === personType || !personType)
    );
  };

  const resetPassword = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/user-credentials/reset-password`,
        { 
          user_id: selectedUser.id,
          new_password: newPassword || null // null = auto generate
        },
        { headers: getAuthHeader() }
      );
      
      // Get the new password from API response
      const newPlainPassword = response.data.new_password;
      
      setCredentialResult({
        name: selectedUser.name || response.data.user_name,
        email: selectedUser.email || response.data.user_email,
        password: newPlainPassword,
        phone: selectedUser.phone || '',
        action: 'reset'
      });
      setCredentialResultDialog(true);
      
      setResetDialog(false);
      setNewPassword('');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const updateEmail = async () => {
    if (!selectedUser || !newEmail) {
      toast.error('Please enter a new email');
      return;
    }
    
    try {
      await axios.put(
        `${API_URL}/api/user-credentials/${selectedUser.id}/update-email?new_email=${encodeURIComponent(newEmail)}`,
        {},
        { headers: getAuthHeader() }
      );
      
      toast.success(`Email updated to ${newEmail}`);
      setEditEmailDialog(false);
      setNewEmail('');
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    }
  };

  const openEditEmail = (user) => {
    setSelectedUser(user);
    setNewEmail(user.email || '');
    setEditEmailDialog(true);
  };

  const createCredential = async () => {
    if (!createForm.person_id) {
      toast.error('Please select a person');
      return;
    }
    
    // Save phone before any state changes - this is critical!
    const savedPhone = createForm.phone || '';
    const savedEmail = createForm.email || '';
    
    try {
      const response = await axios.post(
        `${API_URL}/api/user-credentials/create`,
        createForm,
        { headers: getAuthHeader() }
      );
      
      // Get the plain text password and login_id from API response
      const plainPassword = response.data.password;
      const loginId = response.data.login_id;
      
      setCredentialResult({
        name: response.data.name || '',
        login_id: loginId || '',
        email: response.data.email || savedEmail,
        password: plainPassword,
        phone: savedPhone,
        action: 'create'
      });
      setCredentialResultDialog(true);
      
      setCreateDialog(false);
      setCreateForm({ person_id: '', person_type: 'student', email: '', phone: '', use_admission_number: true });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create credential');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const sendWhatsApp = (phone, name, email, password, role) => {
    // Get tenant-specific login URL
    const schoolCode = localStorage.getItem('school_code') || '';
    const loginUrl = schoolCode ? `${window.location.origin}/${schoolCode}` : `${window.location.origin}/auth`;
    
    // Ensure password is shown
    const passwordToShow = password || 'Password not available';
    
    const message = `🏫 *Cloud Campus Schools*\n\n` +
      `Hello ${name}!\n\n` +
      `Your login credentials:\n` +
      `📧 Email/ID: ${email}\n` +
      `🔑 Password: ${passwordToShow}\n\n` +
      `🔗 Login Link: ${loginUrl}\n\n` +
      `Portal: ${role.charAt(0).toUpperCase() + role.slice(1)} Portal\n\n` +
      `Please change your password after first login.`;
    
    const cleanPhone = phone?.replace(/\D/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone?.startsWith('91') ? cleanPhone : '91' + cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const togglePassword = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const exportCredentials = (type) => {
    let data = [];
    let filename = `${type}_credentials.csv`;
    
    const getCredentialData = (person, role) => {
      const cred = getUserCredential(person.id, role);
      return {
        name: person.name,
        email: cred?.email || person.email || '-',
        phone: person.phone || person.father_mobile || '-',
        role: role,
        has_login: cred ? 'Yes' : 'No'
      };
    };
    
    if (type === 'students') {
      data = students.map(s => getCredentialData(s, 'student'));
    } else if (type === 'teachers') {
      data = teachers.map(t => getCredentialData(t, 'teacher'));
    } else if (type === 'staff') {
      data = staff.map(s => getCredentialData(s, 'staff'));
    }
    
    const csv = [
      ['Name', 'Email', 'Phone', 'Role', 'Has Login'].join(','),
      ...data.map(d => [d.name, d.email, d.phone, d.role, d.has_login].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  const renderUserTable = (data, type) => {
    let filteredData = data;
    
    // Filter by search
    if (searchQuery) {
      filteredData = filteredData.filter(d => 
        d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.phone?.includes(searchQuery) ||
        d.student_id?.includes(searchQuery) ||
        d.employee_id?.includes(searchQuery)
      );
    }
    
    // Filter by class (for students)
    if (type === 'student' && selectedClass !== 'all') {
      filteredData = filteredData.filter(d => d.class_id === selectedClass);
    }
    
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>ID</TableHead>
            <TableHead>Email / Phone</TableHead>
            <TableHead>Login Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                No {type}s found
              </TableCell>
            </TableRow>
          ) : (
            filteredData.map(person => {
              const credential = getUserCredential(person.id, type);
              const hasEmail = person.email || credential?.email;
              const hasPhone = person.phone || person.father_mobile;
              
              return (
                <TableRow key={person.id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{person.name}</p>
                      {type === 'student' && <p className="text-xs text-muted-foreground">{person.class_name}</p>}
                      {type === 'parent' && <p className="text-xs text-muted-foreground">{person.relation} of {person.student_name}</p>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {person.student_id || person.employee_id || '-'}
                    </code>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {hasEmail && (
                        <div className="flex items-center gap-1 text-sm">
                          <Mail className="h-3 w-3" />
                          {hasEmail}
                        </div>
                      )}
                      {hasPhone && (
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Phone className="h-3 w-3" />
                          {hasPhone}
                        </div>
                      )}
                      {!hasEmail && !hasPhone && (
                        <span className="text-xs text-red-500">No contact info</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {credential ? (
                      <Badge className="bg-green-500">
                        <CheckCircle className="h-3 w-3 mr-1" /> Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-600 border-amber-600">
                        <AlertTriangle className="h-3 w-3 mr-1" /> No Login
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {credential ? (
                        <>
                          {hasPhone && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => {
                                // For existing users, send without password - they should use "Forgot Password"
                                const schoolCode = localStorage.getItem('school_code') || '';
                                const loginUrl = schoolCode ? `${window.location.origin}/${schoolCode}` : `${window.location.origin}/auth`;
                                const message = `🏫 *Cloud Campus Schools*\n\nHello ${person.name}!\n\nYour login email: ${credential.email}\n\n🔗 Login: ${loginUrl}\n\nPortal: ${type.charAt(0).toUpperCase() + type.slice(1)} Portal\n\nForgot password? Use "Forgot Password" option on login page.`;
                                const cleanPhone = hasPhone?.replace(/\D/g, '');
                                window.open(`https://wa.me/${cleanPhone?.startsWith('91') ? cleanPhone : '91' + cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
                              }}
                              title="Send Login Info via WhatsApp"
                            >
                              <MessageCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedUser(credential);
                              setResetDialog(true);
                            }}
                            title="Reset Password"
                          >
                            <Key className="h-4 w-4 text-amber-600" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => copyToClipboard(credential.email)}
                            title="Copy Email"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openEditEmail(credential)}
                            title="Edit Email"
                          >
                            <Mail className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Button 
                          size="sm" 
                          onClick={() => {
                            // For parents, extract student_id and relation from the composite ID
                            let actualPersonId = person.id;
                            let parentRelation = null;
                            
                            if (type === 'parent' && person.id.startsWith('parent-')) {
                              actualPersonId = person.student_id;
                              parentRelation = person.relation; // 'Father' or 'Mother'
                            }
                            
                            setCreateForm({
                              person_id: actualPersonId,
                              person_type: type,
                              email: person.email || '',
                              phone: person.phone || person.father_mobile || '',
                              parent_relation: parentRelation,
                              parent_name: type === 'parent' ? person.name : null
                            });
                            setCreateDialog(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Create Login
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    );
  };

  const getStats = () => {
    const studentLogins = students.filter(s => getUserCredential(s.id, 'student')).length;
    const teacherLogins = teachers.filter(t => getUserCredential(t.id, 'teacher')).length;
    const staffLogins = staff.filter(s => getUserCredential(s.id, 'staff')).length;
    const parentLogins = parents.filter(p => getUserCredential(p.id, 'parent')).length;
    
    return { studentLogins, teacherLogins, staffLogins, parentLogins };
  };

  const stats = getStats();

  return (
    <DashboardLayout title="User Credentials">
      <div className="space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.studentLogins}/{students.length}</p>
                  <p className="text-xs text-muted-foreground">Student Logins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg">
                  <UserCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.teacherLogins}/{teachers.length}</p>
                  <p className="text-xs text-muted-foreground">Teacher Logins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-lg">
                  <Briefcase className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.staffLogins}/{staff.length}</p>
                  <p className="text-xs text-muted-foreground">Staff Logins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-lg">
                  <Users className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{parents.length}</p>
                  <p className="text-xs text-muted-foreground">Parents</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle>Manage User Credentials</CardTitle>
                <CardDescription>View, create, and reset login credentials for all users</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => fetchData()}>
                  <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                </Button>
                <Button variant="outline" onClick={() => exportCredentials(activeTab)}>
                  <Download className="h-4 w-4 mr-2" /> Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <TabsList>
                  <TabsTrigger value="students" className="gap-2">
                    <GraduationCap className="h-4 w-4" /> Students
                  </TabsTrigger>
                  <TabsTrigger value="teachers" className="gap-2">
                    <UserCheck className="h-4 w-4" /> Teachers
                  </TabsTrigger>
                  <TabsTrigger value="staff" className="gap-2">
                    <Briefcase className="h-4 w-4" /> Staff
                  </TabsTrigger>
                  <TabsTrigger value="parents" className="gap-2">
                    <Users className="h-4 w-4" /> Parents
                  </TabsTrigger>
                </TabsList>
                
                <div className="flex gap-2">
                  {activeTab === 'students' && (
                    <Select value={selectedClass} onValueChange={setSelectedClass}>
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="All Classes" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Classes</SelectItem>
                        {classes.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-60"
                    />
                  </div>
                </div>
              </div>

              <TabsContent value="students">
                {renderUserTable(students, 'student')}
              </TabsContent>
              
              <TabsContent value="teachers">
                {renderUserTable(teachers, 'teacher')}
              </TabsContent>
              
              <TabsContent value="staff">
                {renderUserTable(staff, 'staff')}
              </TabsContent>
              
              <TabsContent value="parents">
                {renderUserTable(parents, 'parent')}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{selectedUser?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedUser?.email}</p>
            </div>
            <div className="space-y-2">
              <Label>New Password (leave empty for auto-generate)</Label>
              <Input 
                type="text"
                placeholder="Enter new password or leave empty"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetDialog(false)}>Cancel</Button>
            <Button onClick={resetPassword}>
              <Key className="h-4 w-4 mr-2" /> Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={editEmailDialog} onOpenChange={setEditEmailDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{selectedUser?.name}</p>
              <p className="text-sm text-muted-foreground">Current: {selectedUser?.email || 'No email'}</p>
            </div>
            <div className="space-y-2">
              <Label>New Email</Label>
              <Input 
                type="email"
                placeholder="Enter new email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-700 dark:text-yellow-400">
                ⚠️ This will change the login email for this user.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEmailDialog(false)}>Cancel</Button>
            <Button onClick={updateEmail}>
              <Mail className="h-4 w-4 mr-2" /> Update Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Credential Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Login Credential</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Info Box for Students/Parents */}
            {(createForm.person_type === 'student' || createForm.person_type === 'parent') && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-400">
                  <strong>📋 Login ID:</strong> System will automatically use {createForm.person_type === 'student' ? 'Admission Number' : 'P_[Admission Number]'} as Login ID.
                  <br/>
                  <span className="text-xs">Email is optional for students and parents.</span>
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Email (Optional for Students/Parents)</Label>
              <Input 
                type="email"
                placeholder="user@example.com"
                value={createForm.email}
                onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">
                {createForm.person_type === 'student' || createForm.person_type === 'parent' 
                  ? 'Optional - Student/Parent can login using Admission Number' 
                  : 'Required for Teachers/Staff'}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input 
                placeholder="9876543210"
                value={createForm.phone}
                onChange={(e) => setCreateForm({...createForm, phone: e.target.value})}
              />
              <p className="text-xs text-muted-foreground">For WhatsApp notification</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
            <Button onClick={createCredential}>
              <Plus className="h-4 w-4 mr-2" /> Create Credential
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credential Result Dialog - Shows ID & Password after reset/create */}
      <Dialog open={credentialResultDialog} onOpenChange={setCredentialResultDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              {credentialResult.action === 'reset' ? 'Password Reset Successful!' : 'Credential Created Successfully!'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* User Info Card */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-4 rounded-lg border">
              <p className="text-sm text-muted-foreground mb-1">Name</p>
              <p className="font-semibold text-lg">{credentialResult.name}</p>
            </div>
            
            {/* Credentials Box */}
            <div className="bg-slate-900 text-white p-4 rounded-lg space-y-3">
              <div>
                <p className="text-xs text-slate-400 mb-1">🆔 Login ID {credentialResult.login_id ? '(Admission Number)' : ''}</p>
                <div className="flex items-center justify-between bg-slate-800 p-2 rounded">
                  <code className="text-green-400 font-mono text-lg">{credentialResult.login_id || credentialResult.email}</code>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-white hover:text-green-400"
                    onClick={() => copyToClipboard(credentialResult.login_id || credentialResult.email)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              {credentialResult.email && credentialResult.login_id && (
                <div>
                  <p className="text-xs text-slate-400 mb-1">📧 Email (Alternative Login)</p>
                  <div className="flex items-center justify-between bg-slate-800 p-2 rounded">
                    <code className="text-blue-400 font-mono text-sm">{credentialResult.email}</code>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-white hover:text-blue-400"
                      onClick={() => copyToClipboard(credentialResult.email)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              <div>
                <p className="text-xs text-slate-400 mb-1">🔑 Password</p>
                <div className="flex items-center justify-between bg-slate-800 p-2 rounded">
                  <code className="text-yellow-400 font-mono text-xl font-bold tracking-wider">
                    {credentialResult.password}
                  </code>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-white hover:text-yellow-400"
                    onClick={() => copyToClipboard(credentialResult.password)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Copy All Button */}
            <Button 
              className="w-full" 
              variant="outline"
              onClick={() => copyToClipboard(`Login ID: ${credentialResult.login_id || credentialResult.email}\nPassword: ${credentialResult.password}`)}
            >
              <Copy className="h-4 w-4 mr-2" /> Copy Login ID & Password
            </Button>
            
            {/* WhatsApp Share */}
            {credentialResult.phone ? (
              <Button 
                className="w-full bg-green-600 hover:bg-green-700"
                onClick={() => sendWhatsApp(
                  credentialResult.phone, 
                  credentialResult.name, 
                  credentialResult.login_id || credentialResult.email, 
                  credentialResult.password, 
                  'user'
                )}
              >
                <MessageCircle className="h-4 w-4 mr-2" /> Share via WhatsApp
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground text-center">
                Phone number not available. Please copy and share manually.
              </p>
            )}
            
            {/* Warning */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-700 dark:text-amber-400">
                <p className="font-medium">Important!</p>
                <p>Please share this password securely. The user should change it after first login.</p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setCredentialResultDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default UserCredentialsPage;
